import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import PaidIcon from "@mui/icons-material/Paid";
import { Box, Button, Chip, Divider, Grid, Icon, Stack, Typography } from "@mui/material";
import React from "react";
import { globalMaskReal } from "../../../../../shared/utils";
import { Plan } from "./PlanCard";

interface PlanCardHorizontalProps {
	plan: Plan;
	currentPlanId: string;
	onSelect: (plan: Plan) => void;
}

const features = ["Sem fiador", "Assessoria Jurídica", "100% Digital"];

const PlanCardHorizontal: React.FC<PlanCardHorizontalProps> = ({ plan, currentPlanId, onSelect }) => {
	const isCurrentPlan = String(plan.id) === String(currentPlanId);

	return (
		<Box
			sx={{
				bgcolor: "#fff",
				borderRadius: 4,
				boxShadow: "rgba(17, 17, 26, 0.1) 0px 4px 16px, rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 56px;",
				overflow: "hidden",
				border: isCurrentPlan ? "2px solid #8257e6" : "1px solid #f0f0f0",
				mb: 3,
			}}
		>
			<Grid container sx={{ flex: 1 }}>
				{/* Seção Esquerda: Título & Preço */}
				<Grid
					item
					xs={12}
					md={3}
					sx={{
						p: 2,
						background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
						color: "#fff",
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						position: "relative",
					}}
				>
					<Stack spacing={1} alignItems="flex-start">
						{isCurrentPlan && (
							<Chip
								label="Plano Atual"
								size="small"
								sx={{
									bgcolor: "rgba(255, 255, 255, 0.2)",
									color: "#fff",
									fontWeight: 600,
									fontSize: "0.7rem",
									height: 22,
									backdropFilter: "blur(4px)",
									mb: 0.5,
								}}
							/>
						)}

						{plan.commission !== undefined && plan.commission > 0 && (
							<Chip
								icon={<PaidIcon sx={{ fontSize: "16px !important", color: "#7B1FA2 !important" }} />}
								label={`Bônus: ${plan.commission.toLocaleString("pt-br", { style: "currency", currency: "BRL" })}`}
								size="small"
								sx={{
									bgcolor: "#fff",
									color: "#7B1FA2",
									fontWeight: 700,
									fontSize: "0.75rem",
									height: 24,
									mb: 0.5,
									boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
								}}
							/>
						)}

						<Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, fontSize: "1.6rem", textShadow: "0px 2px 4px rgba(0,0,0,0.1)" }}>
							{plan.name}
						</Typography>
						<Box>
							<Typography component="span" sx={{ color: "#fff", fontWeight: 700, fontSize: "1.5rem" }}>
								R$ {globalMaskReal(plan.price.toFixed(2))}
							</Typography>
							<Typography component="span" sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.9rem", ml: 0.5 }}>
								/mês
							</Typography>
						</Box>

						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.9)",
								fontWeight: 500,
							}}
						>
							{plan.flagRateCharge === "1" ? "Aluguel + Encargos" : "Aluguel"}
						</Typography>
					</Stack>
				</Grid>

				{/* Seção do Meio: Dados Técnicos */}
				<Grid item xs={12} md={6} sx={{ p: 2 }}>
					<Stack spacing={1.5}>
						<Box
							sx={{
								bgcolor: "#F8F9FE",
								borderRadius: 3,
								p: 1.5,
							}}
						>
							<Stack
								direction="row"
								divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "#DDE3F2" }} />}
								justifyContent="space-around"
								alignItems="center"
								spacing={2}
							>
								<Stack alignItems="center" spacing={0.5}>
									<Icon
										sx={{
											background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											fontSize: 32,
											mb: 0.5,
										}}
									>
										percent
									</Icon>
									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										Taxa Garanti
									</Typography>
									<Typography variant="subtitle1" fontWeight={800} color="#344767">
										{plan.taxa?.toFixed(2)}%
									</Typography>
								</Stack>

								<Stack alignItems="center" spacing={0.5}>
									<Icon
										sx={{
											background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											fontSize: 32,
											mb: 0.5,
										}}
									>
										home
									</Icon>
									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										LMI
									</Typography>
									<Typography variant="subtitle1" fontWeight={800} color="#344767" sx={{ lineHeight: 1.2 }}>
										{plan.lmi}x
									</Typography>
								</Stack>

								<Stack alignItems="center" spacing={0.5}>
									<Icon
										sx={{
											background: "linear-gradient(135deg, #64B5F6 0%, #9C27B0 100%)",
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											fontSize: 32,
											mb: 0.5,
										}}
									>
										payments
									</Icon>
									<Typography variant="caption" color="text.secondary" fontWeight={500}>
										Valor LMI
									</Typography>
									<Typography variant="subtitle1" fontWeight={800} color="#344767">
										R$ {globalMaskReal(plan.lmiValue.toFixed(2))}
									</Typography>
								</Stack>
							</Stack>
						</Box>

						<Divider />

						<Stack direction="row" spacing={3} flexWrap="wrap">
							{features.map((feature, index) => (
								<Stack key={index} direction="row" spacing={0.5} alignItems="center">
									<CheckIcon sx={{ color: "#4caf50" }} fontSize="small" />
									<Typography variant="body2" fontWeight="500">
										{feature}
									</Typography>
								</Stack>
							))}
						</Stack>
					</Stack>
				</Grid>

				{/* Seção Direita: Ação */}
				<Grid
					item
					xs={12}
					md={3}
					sx={{
						p: 2,
						borderLeft: { md: "1px solid #f0f0f0" },
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<Button
						variant="contained"
						color="primary"
						size="large"
						endIcon={<ArrowForwardIcon />}
						onClick={() => onSelect(plan)}
						sx={{
							borderRadius: 50,
							px: 2.5,
							py: 1,
							fontWeight: 700,
							width: "100%",
							fontSize: "1.1rem",
							backgroundColor: "#8257e6",
							"&:hover": {
								backgroundColor: "#9466ff",
							},
						}}
					>
						{isCurrentPlan ? "RENOVAR" : "CONTRATAR"}
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};

export default PlanCardHorizontal;
